import { useCallback, useEffect, useState } from "react";

import {
  AppConfig,
  DEFAULT_APP_CONFIG,
  normalizeIncomingDoc,
  parseIncomingDoc,
  readInjectedConfig,
} from "../app-config";
import { SentryService } from "../services/SentryService/SentryService";

/**
 * Put the accepted config on the Sentry scope before anything renders against it. Doing
 * this in an effect was too late: effects run child-first, so the widget's own first
 * report went out before the user and tags were set and carried only an IP address.
 */
const acceptIncomingConfig = (
  config: AppConfig,
  source: "injectedObjectJson" | "postMessage"
): AppConfig => {
  SentryService.setWidgetContext(config);
  SentryService.addBreadcrumb({ category: "bridge", message: `config received via ${source}` });

  return config;
};

interface UseIncomingDocConfigResult {
  config: AppConfig;
  ready: boolean;
  setReportLabel: (reportLabel: string) => void;
}

export function useIncomingDocConfig(): UseIncomingDocConfigResult {
  const [config, setConfig] = useState<AppConfig>(() => {
    const injected = readInjectedConfig();

    return injected ? acceptIncomingConfig(injected, "injectedObjectJson") : DEFAULT_APP_CONFIG;
  });
  const [ready, setReady] = useState<boolean>(() => readInjectedConfig() !== null);

  // Backward compatibility: older RN builds send config via postMessage instead of injectedJavaScriptObject
  useEffect(() => {
    if (ready) return;

    const listener = (event: { data: string }): void => {
      if (typeof event.data !== "string") {
        return;
      }

      const doc = parseIncomingDoc(event.data);
      if (!doc) {
        return;
      }

      setConfig(acceptIncomingConfig(normalizeIncomingDoc(doc), "postMessage"));
      setReady(true);
    };

    window.addEventListener("message", listener);
    document.addEventListener("message", listener as never);

    return () => {
      window.removeEventListener("message", listener);
      document.removeEventListener("message", listener as never);
    };
  }, [ready]);

  const setReportLabel = useCallback((reportLabel: string): void => {
    setConfig((currentConfig) =>
      currentConfig.reportLabel === reportLabel ? currentConfig : { ...currentConfig, reportLabel }
    );
  }, []);

  return { config, ready, setReportLabel };
}
